'use client';
/**
 * 현장 상세 맨 아래(관련 글 뒤 · 푸터 앞) «광고 자리 알림» 묶음 — 모든 현장 같은 규칙(site-awareness.ts siteAwarenessMode).
 *   (a) mode=empty    → «{현장명} 광고 자리 · 현재 비어 있음 · 이 현장 광고 신청» → /ads/buy?product=site&site=<id>&from=site_empty
 *       mode=district → «{시군구} 시군구 배너 신청 가능» 한 줄 → /ads/buy?product=district&district=<키>&from=site_slot
 *   (b) 끝 한 줄 «이 현장이나 {시군구} 분양을 홍보하시나요? 현장 광고·시군구 배너 안내 →» → /advertise?from=site_footer
 *       (현장 자체가 판매 불가면 «{시군구} 분양을 홍보하시나요? …» — siteFooterLine)
 *   promo(오픈 기념 쿠폰 스위치 켜짐 · 서버가 정함) + mode=empty → (a) 대신 작은 글 «카더라 안내 · 이 현장 광고 자리» + 3:1 쿠폰 배너
 *       → /advertise?from=promo_site. (b) 끝 한 줄은 그대로.
 *   promo + mode=district(유료 현장 광고 게재·예약 중 — 서버가 promo 를 줄 때만) → 쿠폰 배너는 늘 보인다:
 *       작은 글 «카더라 안내»(자리가 찼으니 «이 현장 광고 자리» 없음) + 같은 3:1 배너를 (a) 자리에, 그 아래 시군구 한 줄 그대로.
 *       유료 현장 광고 요소(띠·와이드·스퀘어·하단 고정)는 위쪽 본문에 그대로 — 이 묶음은 그 뒤(관련 글 뒤 · 푸터 앞).
 *   rules-1031 — 판매 불가 현장(지역주택조합·운영자 영업·하우스 현장 광고 · mode=district/none)도 같은 쿠폰 배너를 늘 낸다(Node 10/31: 배너는 항시 떠 있어야 함).
 * 전부 data-nosnippet · rel="nofollow" · 12~13px. 위 섹션 순서·관심고객 폼·하단 고정 버튼은 건드리지 않는다. 금액·남은 자리 수 없음.
 */
import { buyHref } from '@/lib/ads/buy-link';
import type { SiteAwarenessMode } from '@/lib/ads/site-awareness';
import { trackAdvertiseClick } from './track';
import PromoPicture from '@/components/ads/promo/PromoPicture';
import { PROMO_SITE_CAPTION, PROMO_SITE_CAPTION_TAKEN, PROMO_IMG, promoSlotHref } from '@/lib/ads/promo-slot';

export const SITE_FOOTER_HREF = '/advertise?from=site_footer';

/** 현장 하단 끝 한 줄 — 현장 자체를 팔 수 있을 때만 «이 현장이나» 를 붙인다. */
export function siteFooterLine(siteSellable: boolean, sigungu: string | null | undefined): string {
  const sgg = sigungu?.trim() || '이 지역';
  return siteSellable ? `이 현장이나 ${sgg} 분양을 홍보하시나요? ` : `${sgg} 분양을 홍보하시나요? `;
}


const muted = 'var(--text-tertiary, #64748b)';
const linkStyle: React.CSSProperties = { color: 'var(--text-secondary, #475569)', textDecoration: 'underline', textUnderlineOffset: 2 };

const PROMO_SITE_HREF = promoSlotHref('promo_site');

/** 쿠폰 3:1 배너 줄(작은 글 + 배너) — 빈 자리(empty)와 찬 자리(유료 게재·예약) 모두 같은 틀. */
function PromoSiteBanner({ caption, empty = false }: { caption: string; empty?: boolean }) {
  return (
    <div data-ad-promo="site">
      <div style={{ margin: '0 0 6px', fontSize: 12, lineHeight: '18px', color: muted }}>{caption}</div>
      <a
        href={PROMO_SITE_HREF}
        rel="nofollow"
        data-site-ad-empty={empty ? '' : undefined}
        data-ad-promo-link="promo_site"
        onClick={() => trackAdvertiseClick('promo_site')}
        style={{
          display: 'block', position: 'relative', overflow: 'hidden', borderRadius: 'var(--radius-md, 12px)',
          aspectRatio: `${PROMO_IMG.banner31.width} / ${PROMO_IMG.banner31.height}`, background: 'var(--bg-sunken, #f3f6fb)',
        }}
      >
        <PromoPicture kind="banner31" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
      </a>
    </div>
  );
}

export default function SiteAdAwareness({ siteId, siteName, sigungu, districtKey, mode, siteSellable = true, promo = false }: {
  siteId: string; siteName: string; sigungu: string | null; districtKey: string | null; mode: SiteAwarenessMode;
  /** 이 현장 자체를 팔 수 있나(판매 불가면 끝 한 줄에서 «이 현장이나» 를 뺀다) */
  siteSellable?: boolean;
  /** 오픈 기념 쿠폰 스위치 — 켜지면 빈 현장 광고 줄(mode=empty)이 쿠폰 배너 · mode=district(유료 게재·예약)면 시군구 한 줄 위에 쿠폰 배너 */
  promo?: boolean;
}) {
  const sgg = sigungu?.trim() || null;
  return (
    <div className="kd-acq-aware" data-nosnippet="" data-site-ad-aware={mode} style={{ margin: '24px auto 8px', padding: '0 16px', maxWidth: 720, boxSizing: 'border-box' }}>
      {mode === 'empty' && promo ? (
        <PromoSiteBanner caption={PROMO_SITE_CAPTION} empty />
      ) : mode === 'empty' ? (
        <a
          href={buyHref({ product: 'site', site: siteId, from: 'site_empty' })}
          rel="nofollow"
          data-site-ad-empty=""
          onClick={() => trackAdvertiseClick('site_empty')}
          style={{
            display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '4px 8px', minHeight: 44, boxSizing: 'border-box', padding: '10px 14px',
            border: '1px dashed var(--border, #cbd5e1)', borderRadius: 'var(--radius-md, 12px)', background: 'var(--bg-surface, #fff)',
            color: 'var(--text-primary, #0f172a)', textDecoration: 'none', fontSize: 13, lineHeight: '20px',
          }}
        >
          <strong style={{ fontWeight: 700 }}>{`${siteName} 광고 자리`}</strong>
          <span style={{ color: muted }}>현재 비어 있음 · 누구나 신청 가능</span>
          <span style={{ marginLeft: 'auto', ...linkStyle }}>이 현장 광고 신청 →</span>
        </a>
      ) : null}
      {mode !== 'empty' && promo ? <PromoSiteBanner caption={PROMO_SITE_CAPTION_TAKEN} /> : null /* rules-1031 — 찬 자리·판매 불가(district·none) 모두 카더라 배너 늘 보임 */}
      {mode === 'district' && districtKey && sgg ? (
        <p style={{ margin: promo ? '12px 0 0' : 0, fontSize: 13, lineHeight: '20px', color: muted, textAlign: 'center' }}>
          <a href={buyHref({ product: 'district', district: districtKey, from: 'site_slot' })} rel="nofollow" data-site-ad-district="" onClick={() => trackAdvertiseClick('site_slot')} style={linkStyle}>
            {`${sgg} 시군구 배너 신청 가능`}
          </a>
        </p>
      ) : null}
      <p data-site-ad-footer="" style={{ margin: '12px 0 0', fontSize: 12, lineHeight: '18px', color: muted, textAlign: 'center' }}>
        {siteFooterLine(siteSellable, sgg)}
        <a href={SITE_FOOTER_HREF} rel="nofollow" data-advertise-link="site_footer" onClick={() => trackAdvertiseClick('site_footer')} style={linkStyle}>
          현장 광고·시군구 배너 안내 →
        </a>
      </p>
    </div>
  );
}
